import React, { useEffect, useMemo, useRef, useState } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import apiCall from "store/services/sp.services";
import silApi from "store/services/sil.services";
import { toast } from "react-toastify";
import SweetAlertConfirmationModal from "components/common/SweetAlertConfirmation/SweetAlertConfirmation";
import { parseBooleanVal } from "Utils/utils";
import {
  categoryAppliesToFormModule,
  categoryNamesMatch,
  extractCreatedFormCategory,
  extractFormCategoriesFromResponse,
  normalizeCategoryName,
  splitFormCategories,
} from "./formCategoryUtils";

const normalizeManagedCategories = (response) =>
  extractFormCategoriesFromResponse(response).map((category) => ({
    ...category,
    is_hidden: parseBooleanVal(category?.is_hidden),
  }));

const ManageFormCategoriesModal = ({
  isOpen,
  onClose,
  routeRole = "crm",
  onUpdated,
  focusCreate = false,
}) => {
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const createInputRef = useRef(null);

  const { platform: platformCategories, custom: customCategories } = useMemo(
    () => splitFormCategories(categories),
    [categories]
  );

  const filterBySearch = (items) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((category) =>
      category.name?.toLowerCase().includes(query)
    );
  };

  const filteredPlatformVisible = useMemo(
    () => filterBySearch(platformCategories.filter((category) => !category.is_hidden)),
    [platformCategories, searchQuery]
  );
  const filteredPlatformHidden = useMemo(
    () => filterBySearch(platformCategories.filter((category) => category.is_hidden)),
    [platformCategories, searchQuery]
  );
  const filteredCustomVisible = useMemo(
    () => filterBySearch(customCategories.filter((category) => !category.is_hidden)),
    [customCategories, searchQuery]
  );
  const filteredCustomHidden = useMemo(
    () => filterBySearch(customCategories.filter((category) => category.is_hidden)),
    [customCategories, searchQuery]
  );

  const hasSearchResults =
    filteredPlatformVisible.length +
      filteredPlatformHidden.length +
      filteredCustomVisible.length +
      filteredCustomHidden.length >
    0;

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response =
        routeRole === "sil"
          ? await silApi.silFormCategoriesManageApi()
          : await apiCall.formCategoriesManageRequest({
              role: routeRole,
            });
      setCategories(normalizeManagedCategories(response));
    } catch (error) {
      toast.error(error?.message || "Unable to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setNewCategoryName("");
      setSearchQuery("");
      return;
    }

    loadCategories();
  }, [isOpen, routeRole]);

  useEffect(() => {
    if (!isOpen || !focusCreate) return;
    const timer = setTimeout(() => createInputRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, [isOpen, focusCreate, loading]);

  const toggleSelection = (categoryId) => {
    setSelectedIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = categories.map((category) => category.id);
    if (selectedIds.length === visibleIds.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(visibleIds);
  };

  const handleDeleteCategory = async (confirmed) => {
    if (!confirmed || !categoryToDelete?.uuid) {
      setCategoryToDelete(null);
      return;
    }

    setDeletingCategoryId(categoryToDelete.id);
    setSubmitting(true);
    try {
      const response =
        routeRole === "sil"
          ? await silApi.silDeleteFormCategoryApi({
              uuid: categoryToDelete.uuid,
            })
          : await apiCall.deleteFormCategoryRequest({
              uuid: categoryToDelete.uuid,
              role: routeRole,
            });
      setCategories(normalizeManagedCategories(response));
      setSelectedIds((prev) => prev.filter((id) => id !== categoryToDelete.id));
      onUpdated?.();
      toast.success(`Category "${categoryToDelete.name}" deleted.`);
    } catch (error) {
      toast.error(error?.message || "Unable to delete category.");
    } finally {
      setSubmitting(false);
      setDeletingCategoryId(null);
      setCategoryToDelete(null);
    }
  };

  const updateVisibility = async (isHidden) => {
    if (selectedIds.length === 0) {
      toast.info("Select at least one category.");
      return;
    }

    const selectedLookup = new Set(selectedIds.map((id) => String(id)));
    const previousCategories = categories;

    setCategories((current) =>
      current.map((category) =>
        selectedLookup.has(String(category.id))
          ? { ...category, is_hidden: isHidden }
          : category
      )
    );
    setSelectedIds([]);
    setSubmitting(true);
    try {
      const response =
        routeRole === "sil"
          ? await silApi.silUpdateFormCategoriesVisibilityApi({
              categoryIds: selectedIds,
              isHidden,
            })
          : await apiCall.updateFormCategoriesVisibilityRequest({
              categoryIds: selectedIds,
              isHidden,
              role: routeRole,
            });
      const nextCategories = normalizeManagedCategories(response);
      if (nextCategories.length > 0) {
        setCategories(nextCategories);
      }
      onUpdated?.();
      toast.success(
        isHidden
          ? "Selected categories were hidden."
          : "Selected categories are visible again."
      );
    } catch (error) {
      setCategories(previousCategories);
      setSelectedIds(selectedIds);
      toast.error(error?.message || "Unable to update categories.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCategory = async (event) => {
    event?.preventDefault();
    const name = normalizeCategoryName(newCategoryName);
    if (name.length < 2) {
      toast.error("Category name must be at least 2 characters.");
      createInputRef.current?.focus();
      return;
    }

    const moduleFor = routeRole === "hrm" ? "hrm" : routeRole === "sil" ? "sil" : "crm";
    const moduleLabel = routeRole === "sil" ? "Home MGT" : moduleFor.toUpperCase();
    const existing = categories.find(
      (category) =>
        categoryNamesMatch(category.name, name) &&
        categoryAppliesToFormModule(category, moduleFor)
    );
    if (existing) {
      toast.info(`"${existing.name}" already exists for ${moduleLabel} forms.`);
      setSelectedIds([existing.id]);
      setNewCategoryName("");
      return;
    }

    setCreating(true);
    try {
      const response =
        routeRole === "sil"
          ? await silApi.silCreateFormCategoryApi({ name })
          : await apiCall.createFormCategoryRequest({
              name,
              for: moduleFor,
            });
      const created = extractCreatedFormCategory(response);
      if (!created?.id) {
        throw new Error("Unable to create category.");
      }

      setNewCategoryName("");
      onUpdated?.(created);
      toast.success(`Category "${created.label || created.name}" created.`);
      if (routeRole === "sil") {
        onClose?.();
      } else {
        await loadCategories();
        setSelectedIds([created.id]);
      }
    } catch (error) {
      toast.error(error?.message || "Unable to create category.");
    } finally {
      setCreating(false);
    }
  };

  const renderCategoryGroup = (title, items, emptyHint, { isCustom = false } = {}) => {
    if (items.length === 0 && !emptyHint) {
      return null;
    }

    return (
      <div className='form-category-manage-group'>
        <div className='form-category-manage-group-header'>
          <p className='form-category-manage-group-title'>{title}</p>
          <span className='form-category-manage-group-count'>{items.length}</span>
        </div>
        {items.length === 0 ? (
          <p className='form-category-manage-empty'>{emptyHint}</p>
        ) : (
          <div className='form-category-manage-list'>
            {items.map((category) => (
              <div
                key={category.id}
                className={`form-category-manage-row${
                  category.is_hidden ? " form-category-manage-row--hidden" : ""
                }${isCustom ? " form-category-manage-row--custom" : ""}`}
              >
                <label className='form-category-manage-row-main'>
                  <input
                    type='checkbox'
                    className='form-category-manage-checkbox'
                    checked={selectedIds.includes(category.id)}
                    onChange={() => toggleSelection(category.id)}
                  />
                  <span className='form-category-manage-row-label'>{category.name}</span>
                  {isCustom ? (
                    <span className='form-category-manage-badge form-category-manage-badge--custom'>
                      Yours
                    </span>
                  ) : null}
                  {category.is_hidden ? (
                    <span className='form-category-manage-badge'>Hidden</span>
                  ) : null}
                </label>
                {isCustom ? (
                  <button
                    type='button'
                    className='form-category-manage-delete-btn'
                    onClick={() => setCategoryToDelete(category)}
                    disabled={submitting || creating || deletingCategoryId === category.id}
                    aria-label={`Delete ${category.name}`}
                    title='Delete category'
                  >
                    {deletingCategoryId === category.id ? "Deleting..." : "Delete"}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCategorySection = (sectionTitle, sectionHint, visibleItems, hiddenItems) => {
    if (visibleItems.length === 0 && hiddenItems.length === 0) {
      return null;
    }

    const isCustom = sectionTitle === "Your categories";

    return (
      <section className='form-category-manage-section'>
        <div className='form-category-manage-section-heading'>
          <h3 className='form-category-manage-section-title'>{sectionTitle}</h3>
          <p className='form-category-manage-section-hint'>{sectionHint}</p>
        </div>
        {renderCategoryGroup(
          "Visible",
          visibleItems,
          searchQuery ? "No visible categories match your search." : null,
          { isCustom }
        )}
        {renderCategoryGroup(
          "Hidden",
          hiddenItems,
          searchQuery ? "No hidden categories match your search." : null,
          { isCustom }
        )}
      </section>
    );
  };

  const selectionLabel =
    selectedIds.length === 0
      ? "No categories selected"
      : `${selectedIds.length} selected`;

  return (
    <ModalSkeleton
      isOpen={isOpen}
      onClose={onClose}
      closeModal={onClose}
      title='Form categories'
      cssClass='sm:max-w-xl'
      modalFooter={
        <div className='form-category-manage-footer'>
          <div className='form-category-manage-footer-left'>
            <CustomButton
              type='button'
              variant='secondary'
              label={
                selectedIds.length === categories.length && categories.length > 0
                  ? "Clear selection"
                  : "Select all"
              }
              clickHandler={toggleSelectAll}
              isDisabled={loading || categories.length === 0 || submitting}
            />
            <span className='form-category-manage-selection'>{selectionLabel}</span>
          </div>
          <div className='form-category-manage-footer-actions'>
            <CustomButton
              type='button'
              variant='secondary'
              label='Close'
              clickHandler={onClose}
              isDisabled={submitting || creating}
            />
            <CustomButton
              type='button'
              variant='secondary'
              label='Show selected'
              clickHandler={() => updateVisibility(false)}
              isDisabled={
                loading || submitting || creating || selectedIds.length === 0
              }
              showLoading={submitting}
            />
            <CustomButton
              type='button'
              variant='danger'
              label='Hide selected'
              clickHandler={() => updateVisibility(true)}
              isDisabled={
                loading || submitting || creating || selectedIds.length === 0
              }
              showLoading={submitting}
            />
          </div>
        </div>
      }
    >
      <div className='form-category-manage-body'>
        <p className='form-category-manage-intro'>
          Platform categories are shared defaults from Vertex360. Categories you
          add here belong only to your service provider. Hiding a category removes
          it from filters and form builders without changing existing forms. You
          can permanently delete your own categories when no forms or submissions
          use them.
        </p>

        <form
          className='form-category-manage-create'
          onSubmit={handleCreateCategory}
        >
          <label htmlFor='new-form-category-name' className='form-category-manage-create-label'>
            New category
          </label>
          <div className='form-category-manage-create-row'>
            <input
              id='new-form-category-name'
              ref={createInputRef}
              type='text'
              value={newCategoryName}
              onChange={(event) => setNewCategoryName(event.target.value)}
              placeholder='e.g. Intake forms'
              className='form-category-manage-create-input'
              disabled={creating || loading}
              maxLength={255}
            />
            <CustomButton
              type='submit'
              label='Add category'
              showLoading={creating}
              isDisabled={creating || loading}
            />
          </div>
        </form>

        {categories.length > 3 ? (
          <div className='form-category-manage-search-wrap'>
            <label htmlFor='form-category-search' className='form-category-manage-create-label'>
              Find category
            </label>
            <input
              id='form-category-search'
              type='search'
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder='Search by name'
              className='form-category-manage-create-input'
              disabled={loading}
            />
          </div>
        ) : null}

        {loading ? (
          <div className='form-category-manage-loader'>
            <CircularLoader
              classes='flex justify-center items-center'
              classloader='loader-l'
            />
          </div>
        ) : categories.length === 0 ? (
          <div className='form-category-manage-empty-panel'>
            <p>No categories yet.</p>
            <p>Add your first category above to get started.</p>
          </div>
        ) : !hasSearchResults ? (
          <div className='form-category-manage-empty-panel'>
            <p>No categories match your search.</p>
          </div>
        ) : (
          <>
            {renderCategorySection(
              "Platform categories",
              "Shared defaults supplied by Vertex360. You can hide these if you do not use them.",
              filteredPlatformVisible,
              filteredPlatformHidden
            )}
            {renderCategorySection(
              "Your categories",
              "Created for this service provider only. Other providers cannot see these.",
              filteredCustomVisible,
              filteredCustomHidden
            )}
          </>
        )}
      </div>
      {categoryToDelete ? (
        <SweetAlertConfirmationModal
          isOpen={!!categoryToDelete}
          title='Delete category?'
          dangerMode
          elevatedZIndex
          content={`Delete "${categoryToDelete.name}"? This cannot be undone. Categories with forms, drafts, or submissions assigned to them cannot be deleted.`}
          setIsConfirm={handleDeleteCategory}
        />
      ) : null}
    </ModalSkeleton>
  );
};

export default ManageFormCategoriesModal;
